/**
 * Black Tulip v3 — size finder drawer.
 *
 * Reads the style payload emitted by snippets/v3-spec-json.liquid and answers
 * with window.BTFit. Selecting inside the drawer writes back to the size chips
 * so the answer never has to be remembered or retyped.
 *
 * Renders nothing and binds nothing if the payload is absent — a product
 * without measurements simply has no finder.
 */
(function () {
  'use strict';

  function init(root) {
    var payloadEl = root.querySelector('[data-bt-style]');
    var drawer = root.querySelector('[data-v3-drawer]');
    if (!payloadEl || !drawer || !window.BTFit) return;

    var style;
    try { style = JSON.parse(payloadEl.textContent); } catch (e) { return; }
    if (!style || !style.sizes || !style.lengths) return;

    var scrim   = root.querySelector('[data-v3-scrim]');
    var openers = root.querySelectorAll('[data-v3-finder-open]');
    var closer  = drawer.querySelector('[data-v3-drawer-close]');
    var slider  = drawer.querySelector('[data-v3-foot]');
    var readout = drawer.querySelector('[data-v3-readout]');
    var answerEl = drawer.querySelector('[data-v3-answer]');
    var applyBtn = drawer.querySelector('[data-v3-apply]');
    var chips   = root.querySelectorAll('[data-v3-size]');
    var lastFocus = null;
    var chosen = null;

    var T = (drawer.dataset.strings && JSON.parse(drawer.dataset.strings)) || {};
    function s(key, fallback) { return T[key] || fallback; }

    // INERT, NOT ONLY aria-hidden — 26 September. The closed drawer is moved off
    // screen with a transform, which takes nothing out of the tab order, and
    // aria-hidden only hides it from assistive technology. So on a phone the
    // next three or four Tab stops after "Find your size" were the drawer's own
    // Close, the slider, "Select this size" and the size-guide link — all off
    // screen, no visible focus ring, and announced to a screen reader as not
    // existing while focus sat on them (WCAG 4.1.2, axe aria-hidden-focus).
    // `inert` removes the subtree from focus and from the accessibility tree
    // together. The markup ships inert, so it is right before this script runs.
    //
    // THREE CORRECTIONS FROM THE VERIFICATION PASS, same day:
    //
    // THE OPENER IS RECORDED, NOT INFERRED. This read document.activeElement,
    // but Safari (iOS and macOS) and Firefox on macOS do not focus a button on
    // click or tap — so on those browsers the "last focus" was <body> and
    // closing dropped the shopper's place on the page. The click event says
    // which button opened it; that is what focus returns to.
    //
    // INERT ONLY ONCE IT HAS FINISHED LEAVING, AND A SHIELD UNTIL THEN. Inert
    // content is hit-test transparent: a tap on it lands on whatever is
    // underneath. The first version of this kept the DRAWER a hit target for
    // the slide, which the second verification pass showed was not enough: the
    // drawer eases out so fast that it uncovers "Select this size" about 90ms
    // in, and a double-tap is 120-235ms apart — so the second tap still reached
    // Add to Bag and submitted the form, and a double-tap on Close reached a
    // size chip. The scrim now stays a hit target over the whole page for
    // 350ms (data-closing, v3-pdp.css), whatever is or is not covered by then,
    // and the drawer's own buttons do nothing while it is closing (apply()).
    //
    // FOCUS STAYS INSIDE WHILE IT IS OPEN. It says aria-modal="true" and let Tab
    // walk out behind itself onto the size chart and the Description tab —
    // controls the drawer was covering, so a keyboard user lost sight of focus.
    var inertTimer = null;
    function focusables() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.disabled && el.offsetParent !== null; });
    }
    function open(e) {
      lastFocus = (e && e.currentTarget) || document.activeElement;
      if (inertTimer) { clearTimeout(inertTimer); inertTimer = null; }
      if (scrim) scrim.removeAttribute('data-closing');
      drawer.inert = false;          // before focus(): an inert element refuses it
      drawer.setAttribute('data-open', '');
      if (scrim) scrim.setAttribute('data-open', '');
      drawer.removeAttribute('aria-hidden');
      if (slider) slider.focus();
    }
    function close() {
      if (!drawer.hasAttribute('data-open')) return;
      drawer.removeAttribute('data-open');
      if (scrim) scrim.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      // The shield runs the full 350ms even with reduced motion: the drawer
      // vanishes at once then, but a double-tap is still two taps.
      if (scrim) scrim.setAttribute('data-closing', '');
      inertTimer = setTimeout(function () {
        inertTimer = null;
        if (scrim) scrim.removeAttribute('data-closing');
        if (!drawer.hasAttribute('data-open')) drawer.inert = true;
      }, 350);
    }

    // THE ANSWER NEVER CHANGES HEIGHT — Sumit, 1 October 2026: "this section
    // moves on screen when a comment appears below the recommended size … the
    // box should not jump", and the comment was "barely noticeable".
    //
    // The drawer is anchored to the bottom of the screen, so every pixel the
    // answer gained pushed everything ABOVE it upwards: the slider under her
    // finger moved 43px each time a drag crossed into or out of a tight fit,
    // which on Arbi happens several times between 225 and 285.
    //
    // So the answer is built from fixed rows. Under the size sits one slot, and
    // the slot is always as tall as its tallest possible content: a hidden
    // sizer holds the advice line written out with the widest figures it can
    // carry (and the unavailable line, on a colourway that has a gap), and the
    // real content is laid over it in the same grid cell. The slot's height is
    // the sizer's at every width, so nothing reflows, at any width, and no
    // height is measured or guessed in script.
    //
    // In the slot: the room line when the fit is not tight; when it is, the
    // advice on its own tinted strip with the edge marker, larger and heavier
    // than before. The room line is not repeated above the advice — the advice
    // opens with the same figure ("This leaves 5.0 mm in front of your foot"),
    // and printing it twice was half of why the second sentence went unseen.
    var hasGaps = style.sizes.some(function (z) {
      return (style.offeredSizes && style.offeredSizes.indexOf(z) === -1) ||
             (style.availableSizes && style.availableSizes.indexOf(z) === -1);
    });
    function adviceLine(room, next, nextRoom) {
      return esc(fmt(s('tight_advice',
          'This leaves {room} mm in front of your foot. A {next} leaves {nextRoom} mm, if you would rather have the room.'),
          { room: room, next: next, nextRoom: nextRoom }));
    }
    var sizer = '<div class="v3-answer__note v3-answer__sizer" data-tight aria-hidden="true">' +
        '<div class="v3-answer__line">' + adviceLine('12.5', '8', '19.5') + '</div>' +
        (hasGaps ? '<div class="v3-answer__unavailable">' +
          esc(s('not_in_colour', 'We do not make this colour in that size.')) + '</div>' : '') +
      '</div>';
    // The note is a panel that fills the slot in every state, so the space the
    // advice needs reads as part of the design when there is no advice to give,
    // not as a gap: plain, with the room line, when the fit is not tight;
    // tinted, with the edge marker and the advice, when it is.
    function slot(inner, tight) {
      return '<div class="v3-answer__slot">' + sizer +
        '<div class="v3-answer__note"' + (tight ? ' data-tight' : '') + '>' + inner + '</div>' +
        '</div>';
    }

    function render() {
      var foot = parseInt(slider.value, 10);
      if (readout) readout.textContent = String(foot);
      var a = window.BTFit.answer(style, foot);
      chosen = null;
      if (!a) { answerEl.innerHTML = ''; applyBtn.disabled = true; return; }

      answerEl.removeAttribute('data-tight');

      if (a.beyondRange) {
        answerEl.innerHTML =
          '<div class="v3-answer__k">' + esc(s('your_size', 'Your size')) + '</div>' +
          '<div class="v3-answer__beyond">' + esc(s('beyond', 'Longer than we make')) + '</div>' +
          slot('<div class="v3-answer__line">' +
            esc(fmt(s('fits_up_to', '{style} fits a foot up to {n} mm'),
                    { style: a.styleName, n: a.maxFootMm })) +
          '</div>', false);
        applyBtn.disabled = true;
        return;
      }

      chosen = a.size;
      var html =
        '<div class="v3-answer__k">' +
          esc(fmt(s('your_size_in', 'Your size in {style}'), { style: style.name })) +
        '</div>' +
        '<div class="v3-answer__size">' + esc(a.size.replace('/', ' / ')) + '</div>';
      var inner;

      // Tight: the answer stays the size that fits. The larger size is advice.
      if (a.tight && a.adviceSize) {
        answerEl.setAttribute('data-tight', '');
        // ROOM, NOT SPARE — 17 September, with the nearest rule. spare is measured
        // from the style's toe allowance and under that rule it goes negative, so
        // this line read "only -1.5 mm" on exactly the cases the rule exists to
        // answer. Room is measured from the foot and is never negative for a size
        // the engine answered, so the sentence holds at both ends of the range.
        inner = '<div class="v3-answer__line">' +
          adviceLine(a.roomMm, a.adviceSize.split('/')[0], a.adviceRoomMm) + '</div>';
      } else {
        inner = '<div class="v3-answer__line">' +
          esc(fmt(s('internal_room', '{room} mm in front of your foot'),
                  { internal: a.internalMm, room: a.roomMm })) +
          '</div>';
      }

      // Two different truths. Measured but never made in this colourway is not
      // the same as made and sold out, and saying the wrong one is a lie either
      // way round.
      if (a.offered === false) {
        inner += '<div class="v3-answer__unavailable">' +
          esc(s('not_in_colour', 'We do not make this colour in that size.')) + '</div>';
      } else if (a.available === false) {
        inner += '<div class="v3-answer__unavailable">' +
          esc(s('sold_out_in_size', 'Sold out in that size.')) + '</div>';
      }

      answerEl.innerHTML = html + slot(inner, a.tight && a.adviceSize);
      applyBtn.disabled = a.offered === false || a.available === false;
    }

    function apply() {
      // A tap arriving while the drawer slides out is the second half of a
      // double-tap, not a choice. It hits the drawer and does nothing.
      if (!drawer.hasAttribute('data-open')) return;
      if (!chosen) { close(); return; }
      for (var i = 0; i < chips.length; i++) {
        if (chips[i].dataset.v3Size === chosen && !chips[i].disabled) {
          chips[i].click();
          break;
        }
      }
      close();
    }

    for (var i = 0; i < openers.length; i++) openers[i].addEventListener('click', open);
    if (closer) closer.addEventListener('click', close);
    if (scrim) scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (!drawer.hasAttribute('data-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], at = document.activeElement;
      if (!drawer.contains(at)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && at === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    });
    if (slider) slider.addEventListener('input', render);
    if (applyBtn) applyBtn.addEventListener('click', apply);

    render();
  }

  function esc(v) {
    return String(v).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }
  function fmt(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  /**
   * The inline size selector in the Size & Fit tab (W-20261008-4,
   * snippets/v3-size-inline.liquid). Same engine, same style payload and same
   * approved sentences as the drawer — the strings are read from the drawer's
   * own data-strings, so there is one copy of each and no second seam to keep
   * in step (docs/ci/string-passing-check.js covers the drawer's).
   *
   * The foot goes to BTFit.answer and nowhere else. The chart column is marked
   * from the sizeIndex the engine returns; the foot is never compared with the
   * chart's "Fits a foot up to" row, because the engine's rule (the nearest
   * room to the style's allowance, within a floor) is not "the first column
   * that is long enough". No verdict is written here: the sentences are the
   * drawer's.
   *
   * Un-hides itself only when it can answer. Otherwise the block stays hidden.
   */
  function initInline() {
    var block = document.querySelector('[data-v3-sizeinline]');
    var payloadEl = document.querySelector('[data-bt-style]');
    var drawer = document.querySelector('[data-v3-drawer]');
    if (!block || !payloadEl || !drawer || !window.BTFit) return;

    var style, T;
    try {
      style = JSON.parse(payloadEl.textContent);
      T = JSON.parse(drawer.dataset.strings || '{}');
    } catch (e) { return; }
    if (!style || !style.sizes || !style.lengths) return;
    var KEYS = ['your_size', 'your_size_in', 'internal_room', 'tight_advice', 'beyond', 'fits_up_to'];
    for (var k = 0; k < KEYS.length; k++) if (!T[KEYS[k]]) return;

    var num = block.querySelector('[data-v3-inline-num]');
    var range = block.querySelector('[data-v3-inline-range]');
    var out = block.querySelector('[data-v3-inline-out]');
    var tab = block.closest('#pdp-size-fit') || block.parentNode;
    var chart = tab.querySelector('[data-v3-chart]');
    if (!num || !range || !out) return;
    var MIN = parseInt(num.min, 10), MAX = parseInt(num.max, 10);

    function mark(index) {
      if (!chart) return;
      var prev = chart.querySelectorAll('.v3-chart__match');
      for (var i = 0; i < prev.length; i++) {
        prev[i].classList.remove('v3-chart__match');
        prev[i].removeAttribute('aria-current');
      }
      if (index == null) return;
      var cells = chart.querySelectorAll('[data-size-index="' + index + '"]');
      for (var j = 0; j < cells.length; j++) cells[j].classList.add('v3-chart__match');
      var head = chart.querySelector('thead [data-size-index="' + index + '"]');
      if (head) {
        head.setAttribute('aria-current', 'true');
        // The chart scrolls sideways on a phone: bring the column into view
        // without moving the page.
        var sc = chart.querySelector('[data-v3-edge-scroller]');
        if (sc) {
          var h = head.getBoundingClientRect(), b = sc.getBoundingClientRect();
          if (h.left < b.left) sc.scrollLeft -= (b.left - h.left) + 8;
          else if (h.right > b.right) sc.scrollLeft += (h.right - b.right) + 8;
        }
      }
    }

    function show(foot) {
      var a = (foot >= MIN && foot <= MAX) ? window.BTFit.answer(style, foot) : null;
      if (!a) { out.innerHTML = ''; mark(null); return; }
      if (a.beyondRange) {
        mark(null);
        out.innerHTML =
          '<span class="v3-sizeinline__k">' + esc(T.your_size) + '</span>' +
          '<span class="v3-sizeinline__size">' + esc(T.beyond) + '</span>' +
          '<span class="v3-sizeinline__line">' +
            esc(fmt(T.fits_up_to, { style: a.styleName, n: a.maxFootMm })) + '</span>';
        return;
      }
      mark(a.sizeIndex);
      var line = (a.tight && a.adviceSize)
        ? fmt(T.tight_advice, { room: a.roomMm, next: a.adviceSize.split('/')[0], nextRoom: a.adviceRoomMm })
        : fmt(T.internal_room, { internal: a.internalMm, room: a.roomMm });
      out.innerHTML =
        '<span class="v3-sizeinline__k">' + esc(fmt(T.your_size_in, { style: style.name })) + '</span>' +
        '<span class="v3-sizeinline__size">' + esc(a.size.replace('/', ' / ')) + '</span>' +
        '<span class="v3-sizeinline__line">' + esc(line) + '</span>';
    }

    num.addEventListener('input', function () {
      var v = num.value === '' ? NaN : Number(num.value);
      if (isFinite(v) && v >= MIN && v <= MAX) range.value = String(Math.round(v));
      show(v);
    });
    range.addEventListener('input', function () {
      var v = parseInt(range.value, 10);
      num.value = String(v);
      show(v);
    });
    block.hidden = false;
  }

  function boot() {
    var roots = document.querySelectorAll('[data-v3-pdp]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
    initInline();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
